import React, { type ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { AuthAvatar } from '@/shared/avatar';
import { typography } from '@/shared/theme/tokens';
import { formatPersonName } from '@/shared/utils/personName';
import Box from '../primitives/Box';
import Typography from '../primitives/Typography';

export type StaffIdentityLayout = 'block' | 'inline' | 'text' | 'avatar';

export type StaffIdentitySize = 'sm' | 'md' | 'lg';

export type StaffIdentityPerson = {
  firstName?: string | null;
  lastName?: string | null;
  /** Used for initials and the name line when họ/tên are missing. */
  name?: string | null;
  designation?: string | null;
  avatarUrl?: string | null;
};

export interface StaffIdentityProps {
  person?: StaffIdentityPerson | null;
  /**
   * Name line. A string uses the size's typography.
   * A node is rendered as-is (link, filter cell).
   */
  name?: ReactNode;
  /**
   * Second line. Defaults to `person.designation`.
   * `null` hides the line.
   */
  secondary?: ReactNode | null;
  /** Shown when the second line is empty. Omit to hide an empty line. */
  emptySecondary?: ReactNode;
  /**
   * `block` — avatar, name on top, designation below.
   * `inline` — avatar and name on one line.
   * `text` — name on top, designation below, no avatar.
   * `avatar` — avatar only (closed field adornment; the input already shows the name).
   */
  layout?: StaffIdentityLayout;
  /** sm 20px, md 32px, lg 36px. */
  size?: StaffIdentitySize;
  sx?: SxProps<Theme>;
}

const SIZE = {
  sm: { avatar: 20, fontSize: typography.size.xs, nameVariant: 'body2', secondaryVariant: 'caption', nameWeight: typography.weight.medium, gap: 1 },
  md: { avatar: 32, fontSize: typography.size.sm, nameVariant: 'body2', secondaryVariant: 'caption', nameWeight: typography.weight.medium, gap: 1 },
  lg: { avatar: 36, fontSize: typography.size.sm, nameVariant: 'subtitle2', secondaryVariant: 'body2', nameWeight: typography.weight.semibold, gap: 1.5 },
} as const;

/** Họ + tên initials. Falls back to the first and last token of `name`. */
export function staffPickerInitials(
  person?: {
    firstName?: string | null;
    lastName?: string | null;
    name?: string | null;
  } | null
): string {
  if (!person) return '?';
  const first = person.firstName?.trim().charAt(0) ?? '';
  const last = person.lastName?.trim().charAt(0) ?? '';
  if (first || last) {
    return `${last}${first}`.toUpperCase() || '?';
  }
  const parts = (person.name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  const family = parts[0] ?? '';
  const given = parts[parts.length - 1] ?? '';
  return `${family.charAt(0)}${given.charAt(0)}`.toUpperCase() || '?';
}

function isPlainName(value: ReactNode): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

/**
 * Shared staff face: avatar, name, and designation.
 * List cells use `block` + `lg`. Menus use `block` + `md`.
 * A closed autocomplete adornment uses `avatar` + `sm`.
 */
const StaffIdentity: React.FC<StaffIdentityProps> = ({
  person,
  name,
  secondary,
  emptySecondary,
  layout = 'block',
  size = 'lg',
  sx,
}) => {
  const spec = SIZE[size];
  const formatted = formatPersonName(person) || person?.name?.trim() || '';
  const nameNode = name !== undefined ? name : formatted || '—';
  const designation = person?.designation?.trim() || '';
  const secondaryNode =
    secondary === null
      ? null
      : secondary !== undefined && secondary !== ''
        ? secondary
        : designation || emptySecondary || null;
  const showSecondary = layout === 'block' || layout === 'text';
  const showAvatar = layout !== 'text';
  const showName = layout !== 'avatar';

  const nameLine = !showName ? null : isPlainName(nameNode) ? (
    <Typography
      variant={spec.nameVariant}
      noWrap
      sx={{ fontWeight: spec.nameWeight, lineHeight: 1.25 }}
    >
      {nameNode}
    </Typography>
  ) : (
    nameNode
  );

  const secondaryLine =
    showSecondary && secondaryNode ? (
      isPlainName(secondaryNode) ? (
        <Typography
          variant={spec.secondaryVariant}
          color="text.secondary"
          noWrap
          sx={{ lineHeight: 1.2 }}
        >
          {secondaryNode}
        </Typography>
      ) : (
        secondaryNode
      )
    ) : null;

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          gap: layout === 'text' ? 0 : spec.gap,
          minWidth: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {showAvatar ? (
        <AuthAvatar
          apiSrc={person?.avatarUrl}
          sx={{
            width: spec.avatar,
            height: spec.avatar,
            fontSize: spec.fontSize,
            flexShrink: 0,
          }}
        >
          {staffPickerInitials(person)}
        </AuthAvatar>
      ) : null}
      {nameLine || secondaryLine ? (
        <Box
          sx={{
            minWidth: 0,
            textAlign: 'left',
            // Custom name nodes (e.g. RouterLink <a>) are inline — stack under the name.
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          {nameLine}
          {secondaryLine}
        </Box>
      ) : null}
    </Box>
  );
};

export default StaffIdentity;
